import { msg } from "@lingui/core/macro";
import { createEffect, createSignal, Show } from "solid-js";
import { Dialog, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useDrizzle } from "@/hooks/use-drizzle";
import {
  createListGroupMutation,
  deleteListGroupOptimisticMutation,
  type ListGroup,
  updateListGroupMutation,
} from "@listto/shared";
import { Button } from "@/components/ui/button";
import { Label, TextField } from "@/components/ui/text-field";
import { useSession } from "@/lib/auth";
import { FolderPlus, Trash2 } from "lucide-solid";
import { ConfirmDialog } from "@/components/ui/confirm-dialog";
import { useI18n } from "@/providers/i18n-provider";

interface GroupDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  group?: ListGroup | null;
}

export function GroupDialog(props: GroupDialogProps) {
  const { t } = useI18n();
  const drizzle = useDrizzle();
  const session = useSession();
  const [name, setName] = createSignal("");
  const [isSubmitting, setIsSubmitting] = createSignal(false);

  createEffect(() => {
    setName(props.group?.name ?? "");
  });

  const isEditing = () => Boolean(props.group);

  const handleSubmit = async (e: Event) => {
    e.preventDefault();
    const dbInstance = drizzle();
    const uid = session.userId();
    if (!name().trim() || !dbInstance || !uid) {
      return;
    }

    setIsSubmitting(true);
    try {
      await (props.group
        ? updateListGroupMutation(dbInstance, props.group.id, { name: name().trim() })
        : createListGroupMutation(dbInstance, uid, name().trim()));
      props.onOpenChange(false);
      setName("");
    } catch (err) {
      console.error("Error saving group:", err);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async () => {
    const dbInstance = drizzle();
    if (!dbInstance || !props.group) {
      return;
    }

    try {
      await deleteListGroupOptimisticMutation(dbInstance, props.group.id);
      props.onOpenChange(false);
    } catch (err) {
      console.error("Error deleting group:", err);
    }
  };

  return (
    <Dialog open={props.open} onOpenChange={props.onOpenChange} contentClass="max-w-md p-0">
      <DialogHeader size="md">
        <DialogTitle class="text-xl font-bold">
          {isEditing() ? t(msg`Edit group`) : t(msg`New group`)}
        </DialogTitle>
      </DialogHeader>

      <form onSubmit={(e) => void handleSubmit(e)} class="space-y-6 p-6">
        <div class="flex items-center gap-4">
          <div class="h-16 w-16 rounded-3xl bg-muted flex items-center justify-center text-muted-foreground">
            <FolderPlus class="h-8 w-8" />
          </div>
          <div class="flex-1 space-y-2">
            <Label for="group-name" class="text-xs uppercase tracking-wider font-bold opacity-50">
              {t(msg`Group name`)}
            </Label>
            <TextField
              id="group-name"
              value={name()}
              onInput={(e) => void setName(e.currentTarget.value)}
              placeholder={t(msg`e.g. Work, Personal...`)}
              autofocus
              class="bg-transparent border-none p-0 text-xl font-bold focus-visible:ring-0"
            />
          </div>
        </div>

        <div class="flex items-center justify-between pt-4 border-t border-border/10">
          <Show when={isEditing()}>
            <ConfirmDialog
              title={t(msg`Delete group?`)}
              description={t(
                "Are you sure you want to delete this group? Lists will be kept but no longer grouped.",
              )}
              confirmLabel={t(msg`Delete`)}
              variant="destructive"
              onConfirm={handleDelete}
              renderTrigger={(open) => (
                <Button
                  type="button"
                  variant="ghost"
                  class="text-destructive hover:bg-destructive/10 h-11 rounded-xl px-4"
                  onClick={open}
                >
                  <Trash2 class="h-4 w-4 mr-2" />
                  {t(msg`Delete`)}
                </Button>
              )}
            />
          </Show>
          <div class="flex items-center gap-3 ml-auto">
            <Button
              type="button"
              variant="ghost"
              class="h-11 px-6 rounded-xl font-bold"
              onClick={() => {
                props.onOpenChange(false);
              }}
            >
              {t(msg`Cancel`)}
            </Button>
            <Button
              type="submit"
              disabled={isSubmitting() || !name().trim()}
              class="h-11 px-8 font-bold rounded-xl"
            >
              {isEditing() ? t(msg`Save changes`) : t(msg`Create group`)}
            </Button>
          </div>
        </div>
      </form>
    </Dialog>
  );
}
